import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@components/Button';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import ListenButton from '@components/ListenButton';
import { ONBOARDING_STEP_IDS } from '@components/UserOnboardingTooltip/constants';
import { registerOnboardingAnchor } from '@components/UserOnboardingTooltip/utils';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { Author } from '@interfaces/authors';
import { ClickTarget, ContentType, click } from '@tracking';

import ArticleCommentJumpLink from './ArticleCommentJumpLink';
import ShareArticleModal from './ShareArticleModal';

interface DetailsProps {
  articleId: number;
  audioActive: boolean;
  authors: Author[];
  publishedAt: string;
  commentsCount?: number;
  commenters?: string[];
  canShareArticle: boolean;
  setAudioActive: (audioActive: boolean) => void;
}

const STICKY_NAV_PLUS_SPACE = 100;

const ArticleDetails = ({
  articleId,
  authors,
  publishedAt,
  commentsCount = 0,
  commenters = [],
  canShareArticle,
  audioActive,
  setAudioActive,
}: DetailsProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const { isWebview } = useAppContext();
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    const el = shareButtonRef.current;
    if (canShareArticle && !isWebview && el) {
      registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, el);
      return () => registerOnboardingAnchor(ONBOARDING_STEP_IDS.ArticleShare, null);
    }
  }, [canShareArticle, isWebview]);

  const handleShareClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
    setIsShareModalOpen(true);
  };

  const handleCommentJumpClick = useCallback(() => {
    const commentsSection = document.getElementById('comments-section');
    if (!commentsSection) return;
    const scrollTopDistance = commentsSection.getBoundingClientRect().top;
    window?.scrollBy({
      behavior: 'smooth',
      left: 0,
      top: scrollTopDistance - STICKY_NAV_PLUS_SPACE,
    });
  }, []);

  return (
    <>
      <div className="flex w-full flex-col gap-y-10 border-0 border-b border-solid border-grayscale-gray pb-24 sm:flex-row sm:items-center sm:gap-y-0">
        {authors.length > 0 && (
          <BylineWithHeadshot
            authors={authors}
            authorClassName="author-name-body"
            className="max-w-sm shrink-0 self-start sm:self-auto"
          />
        )}
        <div className="flex shrink-0 items-end justify-end gap-10 self-end sm:ml-auto sm:justify-start sm:self-auto">
          {/* {!isWebview && (
            <ListenButton
              contentType={ContentType.Article}
              isAudioActive={audioActive}
              setIsAudioActive={setAudioActive}
            />
          )} */}
          {canShareArticle && !isWebview && (
            <div ref={shareButtonRef} className="ml-auto" data-onboarding-anchor="article-share">
              <Button
                as="button"
                className="share-button flex items-center rounded-20 border border-transparent bg-grayscale-gray px-16 py-8 font-suisse-intl-mono text-button-xs text-white outline-none print:hidden"
                onClick={handleShareClick}
              >
                Share
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-end justify-end md:justify-between">
        <ClientRenderedContent>
          <time
            className="mb-0 mt-8 block font-suisse-intl-mono text-overline-md text-grayscale-gray lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>
        </ClientRenderedContent>
        {commentsCount > 0 && (
          <ArticleCommentJumpLink
            commentsCount={commentsCount}
            commenters={commenters}
            onClick={handleCommentJumpClick}
          />
        )}
      </div>
      {canShareArticle && !isWebview && (
        <ShareArticleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          articleId={articleId}
        />
      )}
    </>
  );
};

export default ArticleDetails;
